import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'
import type { ContactInfo } from '@/services/types'

function fallbackCopy(text: string): boolean {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()
  try {
    document.execCommand('copy')
    return true
  } catch {
    return false
  } finally {
    document.body.removeChild(textarea)
  }
}

interface ContactListProps {
  contact: ContactInfo
  /** 'card' for light bg, 'dark' for blue/dark bg */
  variant?: 'card' | 'dark'
  className?: string
}

interface ContactItem {
  type: string
  icon: string
  label: string
  value: string
  action: 'open' | 'copy'
  href?: string
}

export default function ContactList({ contact, variant = 'card', className }: ContactListProps) {
  const { t } = useTranslation()
  const [copiedType, setCopiedType] = useState<string | null>(null)

  const items: ContactItem[] = []

  if (contact.whatsapp) {
    items.push({
      type: 'whatsapp',
      icon: 'chat',
      label: t('contact.whatsapp'),
      value: contact.whatsapp,
      action: 'open',
      href: `https://wa.me/${contact.whatsapp.replace(/[^0-9]/g, '')}`,
    })
  }
  if (contact.telegram) {
    items.push({
      type: 'telegram',
      icon: 'send',
      label: t('contact.telegram'),
      value: contact.telegram,
      action: 'open',
      href: `https://t.me/${contact.telegram.replace(/^@/, '')}`,
    })
  }
  if (contact.wechat) {
    items.push({
      type: 'wechat',
      icon: 'forum',
      label: t('contact.wechat'),
      value: contact.wechat,
      action: 'copy',
    })
  }
  if (contact.email) {
    items.push({
      type: 'email',
      icon: 'email',
      label: t('contact.email'),
      value: contact.email,
      action: 'open',
      href: `mailto:${contact.email}`,
    })
  }

  if (items.length === 0) return null

  const handleCopy = (item: ContactItem) => {
    const onSuccess = () => {
      setCopiedType(item.type)
      setTimeout(() => setCopiedType(null), 2000)
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(item.value).then(onSuccess).catch(() => {
        fallbackCopy(item.value) && onSuccess()
      })
    } else {
      fallbackCopy(item.value) && onSuccess()
    }
  }

  const isDark = variant === 'dark'

  const itemBg = isDark
    ? 'bg-white/15 hover:bg-white/25'
    : 'bg-white hover:bg-neutral-50 border border-neutral-200'

  const labelColor = isDark ? 'text-white/70' : 'text-neutral-500'
  const valueColor = isDark ? 'text-white' : 'text-neutral-900'

  const iconColors: Record<string, string> = isDark
    ? { whatsapp: 'text-green-300', telegram: 'text-sky-300', wechat: 'text-emerald-300', email: 'text-blue-200' }
    : { whatsapp: 'text-green-600', telegram: 'text-sky-600', wechat: 'text-emerald-600', email: 'text-blue-600' }

  const iconBgs: Record<string, string> = isDark
    ? { whatsapp: 'bg-green-500/20', telegram: 'bg-sky-500/20', wechat: 'bg-emerald-500/20', email: 'bg-blue-400/20' }
    : { whatsapp: 'bg-green-50', telegram: 'bg-sky-50', wechat: 'bg-emerald-50', email: 'bg-blue-50' }

  const btnStyles: Record<string, string> = isDark
    ? {
        whatsapp: 'bg-green-500 hover:bg-green-400 text-white',
        telegram: 'bg-sky-500 hover:bg-sky-400 text-white',
        wechat: 'bg-emerald-500 hover:bg-emerald-400 text-white',
        email: 'bg-white/20 hover:bg-white/30 text-white border border-white/30',
      }
    : {
        whatsapp: 'bg-green-600 hover:bg-green-700 text-white',
        telegram: 'bg-sky-600 hover:bg-sky-700 text-white',
        wechat: 'bg-emerald-600 hover:bg-emerald-700 text-white',
        email: 'bg-blue-600 hover:bg-blue-700 text-white',
      }

  return (
    <div className={clsx('flex flex-col gap-3', className)}>
      {items.map((item) => (
        <div
          key={item.type}
          className={clsx(
            'flex items-center gap-3 rounded-xl px-4 py-3 transition-all',
            itemBg
          )}
        >
          {/* Icon */}
          <div
            className={clsx(
              'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
              iconBgs[item.type]
            )}
          >
            <span className={clsx('material-icons-round text-xl', iconColors[item.type])}>
              {item.icon}
            </span>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className={clsx('text-xs font-medium', labelColor)}>{item.label}</div>
            <div className={clsx('text-sm font-semibold truncate', valueColor)}>
              {item.value}
            </div>
          </div>

          {/* Action */}
          {item.action === 'open' ? (
            <a
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className={clsx(
                'flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all',
                btnStyles[item.type]
              )}
            >
              <span className="material-icons-round text-sm">open_in_new</span>
              {t('contact.open')}
            </a>
          ) : (
            <button
              onClick={() => handleCopy(item)}
              className={clsx(
                'flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all',
                copiedType === item.type
                  ? 'bg-green-500 text-white'
                  : btnStyles[item.type]
              )}
            >
              <span className="material-icons-round text-sm">
                {copiedType === item.type ? 'check' : 'content_copy'}
              </span>
              {copiedType === item.type ? t('contact.copied') : t('contact.copy')}
            </button>
          )}
        </div>
      ))}
    </div>
  )
}
